import React from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAdminStore } from '@/lib/admin-store';
import { useTranslation } from '@/hooks/useTranslation';
import ArtworkCard from '@/components/ArtworkCard';

const ArtistDetailPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const { t, localize } = useTranslation();
  const allArtists = useAdminStore((s) => s.artists);
  const allArtworks = useAdminStore((s) => s.artworks);

  const artist = allArtists.find((a) => a.slug === slug);
  const artworks = allArtworks.filter((w) => {
    if (typeof w.artist === 'string') return false;
    return w.artist._id === artist?._id;
  });

  if (!artist) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-24">
        <p className="text-black/30 dark:text-white/30">Artist not found</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 sm:pt-32 pb-16 sm:pb-20 px-4 sm:px-6 md:px-24">
      <div className="max-w-7xl mx-auto">
        {/* Artist Header */}
        <div className="flex flex-col md:flex-row gap-8 sm:gap-12 md:gap-16 mb-12 sm:mb-24 md:items-start">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8 }}
            className="w-full max-w-[280px] mx-auto md:max-w-none md:mx-0 md:w-80 aspect-[3/4] rounded-2xl overflow-hidden bg-[#f9f9f9] dark:bg-[#0a0a0a] border border-black/5 dark:border-white/5 flex-shrink-0"
          >
            {artist.portrait ? (
              <img
                src={artist.portrait}
                alt={localize(artist.name)}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-black/10 dark:text-white/10">
                <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
              </div>
            )}
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.8 }}
            className="flex-1"
          >
            <span className="text-[9px] sm:text-[10px] font-bold tracking-[0.4em] sm:tracking-[0.5em] text-black/20 dark:text-white/20 uppercase mb-3 sm:mb-4 block">
              {t.artists.title}
            </span>
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-syncopate font-bold text-black dark:text-white uppercase tracking-tighter leading-none mb-4 sm:mb-8">
              {localize(artist.name)}
            </h1>
            <p className="text-base sm:text-lg md:text-xl text-black/50 dark:text-white/50 font-light leading-relaxed max-w-2xl whitespace-pre-line text-justify">
              {localize(artist.bio)}
            </p>
            <p className="text-[9px] sm:text-[10px] font-bold uppercase tracking-widest text-black/30 dark:text-white/30 mt-4 sm:mt-6">
              {artworks.length} {t.artists.works}
            </p>
          </motion.div>
        </div>

        {/* Artworks Grid */}
        <div>
          <h2 className="text-2xl sm:text-3xl font-syncopate font-bold text-black dark:text-white uppercase tracking-tighter mb-6 sm:mb-12">
            {t.artists.works}
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 sm:gap-6 md:gap-8">
            {artworks.map((artwork) => (
              <ArtworkCard key={artwork._id} artwork={artwork} />
            ))}
          </div>
          {artworks.length === 0 && (
            <p className="text-black/30 dark:text-white/30 text-center py-20">
              No artworks available
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default ArtistDetailPage;
